import {
  CustomerContextProductAttributes,
  PricePointAttributes,
} from 'api-client'
import { pick } from 'ramda'

import { UFn } from '~shared-components/hooks/useUom'
import {
  Price,
  pricePointAttributesToPriceTier,
} from '~shared-components/transforms/price.transforms'
import { CustomerContext } from '~shared-components/transforms/user.transforms'
import { ifArrayReturnNthItem } from '~shared-components/utils/functional'
import {
  formatPrice,
  getLowestTierPrice,
} from '~shared-components/utils/price-utils'

import { Orderline } from '../model/order.model'
import {
  APIProduct,
  Product,
  ProductData,
  ProductDetailData,
} from '../model/product.model'

export const transformOrderlineProductToProductData = (
  product: Orderline['product'],
): ProductData => ({
  id: product.id,
  isDangerous: (product.dangerousGoodsClass?.length || 0) > 0,
  isHazardous: Boolean(product.salesGroupProducts?.some(sgp => sgp.hazardous)),
  name: product.name,
  packaging: product.packaging,
  sourceId: product.number,
})

export const formatPricepoint = (
  u: UFn,
  pricepoint: PricePointAttributes | undefined,
) =>
  pricepoint !== undefined
    ? `${formatPrice(Price.fromMonetaryAmount(pricepoint.price))} / ${
        pricepoint.pricingUnit?.quantity
      } ${u(pricepoint.pricingUnit.uom)}`
    : ''

const pricePointKeys: (keyof PricePointAttributes)[] = [
  'price',
  'pricingUnit',
  'scaleStart',
  'validFrom',
  'validUntil',
  'isActive',
]
const customerContextProductKeys: (keyof CustomerContextProductAttributes)[] = [
  'number',
]

const baseCartProductDataKeys: (keyof ProductData)[] = [
  'customerProductId',
  'id',
  'isDangerous',
  'isHazardous',
  'name',
  'packaging',
  'sourceId',
]

export const transformAPIProductToProduct = (
  apiProduct: APIProduct,
): Product => {
  const customerProductId = ifArrayReturnNthItem(
    apiProduct.customerContextProducts,
  )?.number

  const pricePoints = apiProduct.pricePoints?.map(pick(pricePointKeys)) || []
  const lowestTierPrice = getLowestTierPrice(pricePoints)
  const priceTier = lowestTierPrice
    ? pricePointAttributesToPriceTier(lowestTierPrice)
    : undefined

  return {
    ...apiProduct,
    customerContextProducts: apiProduct.customerContextProducts.map(
      pick(customerContextProductKeys),
    ),
    customerProductId,
    dangerousGoodsClass: apiProduct.dangerousGoodsClass || [],
    invoiceLink: '',
    isDangerous: !!apiProduct.dangerousGoodsClass?.length,
    isHazardous: Boolean(
      apiProduct.salesGroupProducts?.some(sgp => sgp.hazardous),
    ),
    pricePoints,
    priceTier,
    showDetailsButton: true,
    sourceId: apiProduct.number,
  }
}

export const transformProductToBaseCartProduct = (
  product: ProductDetailData,
  customerContext: CustomerContext,
  quantity: number | undefined,
) => ({
  customerContext,
  data: pick(baseCartProductDataKeys, product),
  pricePoints: product.pricePoints,
  quantity,
})
